import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SessionTemplate } from "../../domain/SessionTemplate";
import { listSessionTemplates } from "../../application/sessionTemplates";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import { getTemplateName } from "../utils/templateLocalization";

interface TemplateSelectorProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    /** Pre-seed the currently selected template so it shows immediately before async load */
    currentTemplate?: SessionTemplate | null;
}

export default function TemplateSelector({ value, onChange, className, currentTemplate }: TemplateSelectorProps) {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState<{ value: string; label: string }[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const fetchTemplates = async () => {
            try {
                const data = await listSessionTemplates();
                if (mounted) {
                    const options = data.map((tpl: SessionTemplate) => ({
                        value: tpl.id,
                        label: getTemplateName(tpl, t),
                    }));
                    setTemplates(options);
                }
            } catch (err) {
                console.error("Failed to load templates", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchTemplates();
        return () => {
            mounted = false;
        };
    }, [t]);

    // Pre-seed: if currentTemplate is provided and not yet in the loaded list, add it so the
    // selected value is shown immediately before the async fetch completes.
    const allOptions = (() => {
        const base = [...templates];
        if (currentTemplate?.id && !templates.find(t => t.value === currentTemplate.id)) {
            return [{ value: currentTemplate.id!, label: getTemplateName(currentTemplate, t) }, ...templates];
        }
        return base;
    })();

    return (
        <div className={className}>
            <FilterSelect
                options={allOptions}
                value={value || ""}
                onChange={(val: string | string[]) => onChange(val as string)}
                placeholder={loading ? t("common.loading") : t("templates.selectTemplate", "Select Template")}
                searchPlaceholder={t("common.search")}
            />
        </div>
    );
}
